import { Box, IconButton, Textarea, Text, Spinner } from "@chakra-ui/react"
import { IoMdSend } from "react-icons/io";
import { useColorPalette } from "./color-palette-context";
import { useColorModeValue } from "./color-mode";
import { useEffect, useRef, useState } from "react";
import { Message } from "../chat-history"
import Markdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'

/**
 * Props for the Chatbot component.
 */
interface ChatbotProps {
    /** The width of the chatbot container. */
    width?: number | string;
    /** The height of the chatbot container. */
    height?: number | string;
    /** The font family to use for text within the chatbot. */
    fontFamily?: string;
    /** The list of messages to display in the chat window. */
    messages: Message[];
    /** Callback function triggered when a message is sent. */
    onEnter: (message: string) => void;
    /** The current value of the message input field. */
    inputValue: string;
    /** Function to update the message input field value. */
    setInputValue: (value: string) => void;
    /** Whether the chatbot is currently processing a response. */
    isThinking?: boolean;
}

/**
 * A component that renders a chat interface, including message history and an input area.
 * Supports markdown rendering for bot responses and handles user input via keyboard or send button.
 * 
 * @param {ChatbotProps} props - The component props.
 * @returns {JSX.Element} The rendered chatbot interface.
 */
export const Chatbot = (props: ChatbotProps) => {
    const {
        width,
        height,
        fontFamily,
        messages,
        onEnter,
        inputValue,
        setInputValue,
        isThinking
    } = props;
    const { colorPalette } = useColorPalette();
    const inputBGcolor = useColorModeValue('var(--chakra-colors-bg-muted)', 'var(--chakra-colors-bg-emphasized)');
    const botFontColor = useColorModeValue(`var(--chakra-colors-fg)`, `var(--chakra-colors-fg)` );
    const userFontColor = useColorModeValue(`var(--chakra-colors-fg)`, `var(--chakra-colors-fg)` );
    const sendBtnDisabled = inputValue.trim() === '' || isThinking;
    const userBGColor = useColorModeValue(`${colorPalette}.subtle`, `${colorPalette}.muted`);
    
    const scrollRef = useRef<HTMLDivElement>(null);

    /**
     * Auto-scrolls to the bottom of the chat window when new messages arrive or thinking state changes.
     */
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, isThinking]);

    /**
     * Handles key down events in the textarea.
     * Sends the message on 'Enter' (without Shift).
     */
    const handleKeyDown = (e : React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) { // Allow Shift+Enter for new line
            e.preventDefault();
            if (inputValue.trim() && !isThinking) {
                onEnter(inputValue);
                setInputValue('');
            }
        }
    }

    /**
     * Handles the send button click event.
     */
    const handleSendButton = (e : React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
            if (inputValue.trim() && !isThinking) {
            onEnter(inputValue);
            setInputValue('');
        }
    }

    /**
     * Updates the input value state as the user types.
     */
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInputValue(e.target.value);
    }

    /**
     * Custom components for react-markdown to use Chakra UI components for styling.
     */
    const markdownComponents = {
        p: (props: any) => <Text {...props} />,
        ul: (props: any) => <Box as="ul" ml="6" mb="2" style={{ listStyleType: 'disc' }} {...props} />,
        ol: (props: any) => <Box as="ol" ml="6" mb="2" style={{ listStyleType: 'decimal' }} {...props} />,
        li: (props: any) => <Box as="li" mb="1" {...props} />,
        a: (props: any) => <Box as="a" color={`${colorPalette}.solid`} textDecoration="underline" {...props} />,
        h1: (props: any) => <Text fontSize="2xl" fontWeight="bold" mb="2" {...props} />,
        h2: (props: any) => <Text fontSize="xl" fontWeight="bold" mb="2" {...props} />,
        h3: (props: any) => <Text fontSize="lg" fontWeight="bold" mb="2" {...props} />,
        code: (props: any) => <Box as="code" bg="var(--chakra-colors-bg-muted)" p="1" borderRadius="sm" {...props} />,
        pre: (props: any) => <Box as="pre" bg="var(--chakra-colors-bg-muted)" p="2" borderRadius="md" overflowX="auto" mb="2" {...props} />,
    };

    return (
        <Box display={'flex'} flexDir={'column'} width={'100%'} height={'100%'} overflow={"hidden"} boxSizing={"border-box"}>
            {/* Chat Window */}
           <Box ref={scrollRef} display={'flex'} flexDir={'column'} minHeight={0} flexGrow={'1'} overflowY="auto" p="4">
               <Box flex="1" />
               {messages.map((msg, index) => (
                   <Box
                       key={index}
                       display="flex"
                       justifyContent={msg.type === 'user' ? 'flex-end' : 'flex-start'}
                       mb="2"
                   >
                       <Box
                           bg={msg.type === 'user' ? userBGColor : ''}
                           color={msg.type === 'user' ? userFontColor : botFontColor}
                           px="4"
                           py="2"
                           borderRadius="var(--chakra-radii-lg) 0 var(--chakra-radii-lg) var(--chakra-radii-lg)"
                           maxW="70%"
                           fontFamily={props.fontFamily}
                       >
                           <Box fontSize={'14px'} textAlign={"left"} fontFamily={props.fontFamily}>
                               <Markdown rehypePlugins={[rehypeRaw]} components={markdownComponents}>{msg.text}</Markdown>
                           </Box>
                       </Box>
                   </Box>
               ))}
               {isThinking && (
                   <Box
                       display="flex"
                       justifyContent="flex-start"
                       mb="2"
                   >
                       <Box
                           color={botFontColor}
                           px="4"
                           py="2"
                           borderRadius="var(--chakra-radii-lg) 0 var(--chakra-radii-lg) var(--chakra-radii-lg)"
                           maxW="70%"
                           fontFamily={props.fontFamily}
                       >
                           <Text fontSize={'14px'} color={'var(--chakra-colors-fg-muted)'} textAlign={"left"} fontFamily={props.fontFamily} display="flex" alignItems="center" gap="2">
                               Thinking <Spinner size="sm" />
                           </Text>
                       </Box>
                   </Box>
               )}
           </Box>
            {/* Input Section */}
            <Box display={'flex'} gap={'2px'} justifyContent={'center'} m={'16px 24px 24px 24px'} alignItems={'center'} >
                <Box p={'16px 32px 0px 32px'} borderRadius={'l2'} backgroundColor={inputBGcolor} width={'100%'} display={'flex'} >
                    <Textarea 
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        p={0}
                        m={0}
                        size={'md'}
                        outline={'none'}
                        border={'none'}
                        fontFamily={fontFamily}
                        placeholder="Ask something..."
                        autoresize
                        maxH="10lh"
                        overflowY="auto"
                        disabled={isThinking}
                    />
                </Box>
                <IconButton aria-label="Send message" onClick={handleSendButton} disabled={sendBtnDisabled} mb={'6px'} size={'lg'} ml={'0.5rem'} backgroundColor={`${colorPalette}.solid`} alignSelf={'flex-end'} >
                    <IoMdSend />
                </IconButton>
            </Box>
        </Box>
    )
}
